import React, { useState, useRef, useEffect } from "react";
import "./PhoneInput.css";

const PhoneInput = ({
  label,
  name,
  value,
  onChange,
  onCountryChange,
  placeholder = "",
  error,
  container = null, // Container para renderizar o dropdown
}) => {
  const [countryCode, setCountryCode] = useState("55");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [localNumber, setLocalNumber] = useState("");
  const dropdownRef = useRef(null);

  // Lista de países com bandeira e código
  const countries = [
    { code: "55", name: "Brasil", flag: "🇧🇷" },
    { code: "1", name: "Estados Unidos", flag: "🇺🇸" },
    { code: "1", name: "Canadá", flag: "🇨🇦" },
    { code: "54", name: "Argentina", flag: "🇦🇷" },
    { code: "56", name: "Chile", flag: "🇨🇱" },
    { code: "57", name: "Colômbia", flag: "🇨🇴" },
    { code: "52", name: "México", flag: "🇲🇽" },
    { code: "51", name: "Peru", flag: "🇵🇪" },
    { code: "598", name: "Uruguai", flag: "🇺🇾" },
    { code: "595", name: "Paraguai", flag: "🇵🇾" },
    { code: "591", name: "Bolívia", flag: "🇧🇴" },
    { code: "593", name: "Equador", flag: "🇪🇨" },
    { code: "502", name: "Guatemala", flag: "🇬🇹" },
    { code: "504", name: "Honduras", flag: "🇭🇳" },
    { code: "506", name: "Costa Rica", flag: "🇨🇷" },
    { code: "507", name: "Panamá", flag: "🇵🇦" },
    { code: "501", name: "Belize", flag: "🇧🇿" },
    { code: "503", name: "El Salvador", flag: "🇸🇻" },
    { code: "505", name: "Nicarágua", flag: "🇳🇮" },
    { code: "44", name: "Reino Unido", flag: "🇬🇧" },
    { code: "33", name: "França", flag: "🇫🇷" },
    { code: "49", name: "Alemanha", flag: "🇩🇪" },
    { code: "39", name: "Itália", flag: "🇮🇹" },
    { code: "34", name: "Espanha", flag: "🇪🇸" },
    { code: "351", name: "Portugal", flag: "🇵🇹" },
    { code: "31", name: "Holanda", flag: "🇳🇱" },
    { code: "32", name: "Bélgica", flag: "🇧🇪" },
    { code: "41", name: "Suíça", flag: "🇨🇭" },
    { code: "43", name: "Áustria", flag: "🇦🇹" },
    { code: "46", name: "Suécia", flag: "🇸🇪" },
    { code: "45", name: "Dinamarca", flag: "🇩🇰" },
    { code: "47", name: "Noruega", flag: "🇳🇴" },
    { code: "358", name: "Finlândia", flag: "🇫🇮" },
    { code: "48", name: "Polônia", flag: "🇵🇱" },
    { code: "353", name: "Irlanda", flag: "🇮🇪" },
    { code: "30", name: "Grécia", flag: "🇬🇷" },
    { code: "81", name: "Japão", flag: "🇯🇵" },
    { code: "86", name: "China", flag: "🇨🇳" },
    { code: "82", name: "Coreia do Sul", flag: "🇰🇷" },
    { code: "91", name: "Índia", flag: "🇮🇳" },
    { code: "61", name: "Austrália", flag: "🇦🇺" },
    { code: "64", name: "Nova Zelândia", flag: "🇳🇿" },
    { code: "7", name: "Rússia", flag: "🇷🇺" },
    { code: "971", name: "Emirados Árabes", flag: "🇦🇪" },
    { code: "966", name: "Arábia Saudita", flag: "🇸🇦" },
    { code: "20", name: "Egito", flag: "🇪🇬" },
    { code: "27", name: "África do Sul", flag: "🇿🇦" },
    { code: "972", name: "Israel", flag: "🇮🇱" },
    { code: "971", name: "Catar", flag: "🇶🇦" },
    { code: "971", name: "Kuwait", flag: "🇰🇼" },
  ];

  // Efeito para processar o valor inicial e detectar o código do país
  useEffect(() => {
    if (value) {
      // Só tenta detectar o país se o valor começar com +
      if (value.startsWith('+')) {
        const cleaned = value.replace(/\D/g, "");

        // Tenta detectar o código do país
        let detectedCountryCode = "55"; // default
        let phoneNumber = cleaned;

        // Ordena códigos por tamanho (maior primeiro) para evitar matches incorretos
        const sortedCountries = [...countries].sort((a, b) => b.code.length - a.code.length);

        for (const country of sortedCountries) {
          if (cleaned.startsWith(country.code)) {
            detectedCountryCode = country.code;
            phoneNumber = cleaned.slice(country.code.length);
            break;
          }
        }

        setCountryCode(detectedCountryCode);
        setLocalNumber(phoneNumber);

        // Notifica o componente pai sobre a mudança do código do país
        if (onCountryChange) {
          onCountryChange(detectedCountryCode);
        }
      } else {
        // Se não começar com +, assume que é apenas o número local
        const cleaned = value.replace(/\D/g, "");
        setLocalNumber(cleaned);
      }
    } else {
      setLocalNumber("");
    }
  }, [value]);

  const formatLocalNumber = (number, code) => {
    if (!number) return "";

    // Limita o tamanho dependendo do país
    let maxDigits = code === "55" ? 11 : 15;
    const limited = number.replace(/\D/g, "").slice(0, maxDigits);

    // Formata conforme o país
    if (code === "55") {
      // Formato brasileiro: (XX) XXXXX-XXXX
      if (limited.length <= 2) {
        return limited;
      } else if (limited.length <= 7) {
        return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
      } else {
        return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7, 11)}`;
      }
    } else {
      // Outros países - sem formatação
      return limited;
    }
  };

  const handleChange = (e) => {
    let rawValue = e.target.value;

    // Remove tudo exceto dígitos
    const cleaned = rawValue.replace(/\D/g, "");

    // Armazena apenas os dígitos do número local
    setLocalNumber(cleaned);

    // Formata o número local para exibição
    const formatted = formatLocalNumber(cleaned, countryCode);

    // Atualiza apenas o número local (sem código do país)
    // O valor completo será montado quando necessário
    onChange(formatted);
  };

  const handleCountrySelect = (country) => {
    setCountryCode(country.code);
    setIsDropdownOpen(false);

    // Notifica o componente pai sobre a mudança do código do país
    if (onCountryChange) {
      onCountryChange(country.code);
    }
  };

  const selectedCountry = countries.find((c) => c.code === countryCode) || countries[0];

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="phoneInputWrapper">
      {label && (
        <label className="phoneInputLabel" htmlFor={name}>
          {label}
        </label>
      )}
      <div className="phoneWrapper">
        <div className="phoneInputContainer">
          <div
            className="countrySelector"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDropdownOpen(!isDropdownOpen);
            }}
          >
            <span className="countryFlag">{selectedCountry.flag}</span>
            <span className="countryCode">+{selectedCountry.code}</span>
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L6 6L11 1"
                stroke="#211E1F"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          {isDropdownOpen && (
            <div className="countryDropdown" ref={dropdownRef}>
              {countries.map((country) => (
                <div
                  key={country.code}
                  className={`countryOption ${country.code === countryCode ? "selected" : ""
                    }`}
                  onClick={() => handleCountrySelect(country)}
                >
                  <span className="countryFlag">{country.flag}</span>
                  <span className="countryName">{country.name}</span>
                  <span className="countryCodeValue">+{country.code}</span>
                </div>
              ))}
            </div>
          )}
          <input
            type="text"
            name={name}
            className="phoneInput"
            placeholder={placeholder}
            value={formatLocalNumber(localNumber, countryCode)}
            onChange={handleChange}
            inputMode="numeric"
          />
        </div>
        {error && <div className="phoneError">{error}</div>}
      </div>
    </div>
  );
};

export default PhoneInput;

